import Component from '@glimmer/component'
import { reportingTitle, reportingToolTip as toolTipConfig } from 'teacher-ui-client/utils/giraffe/highcharts-config'
import { colors } from '@colors'
import BaseHighcharterComponent from 'teacher-ui-client/components/base/highcharter'
import type Highcharts from 'highcharts'

interface Args {
  animation?: boolean
  categories?: string[]
  chartMargin?: (number | null)[]
  chartSpacing?: [number, number, number, number]
  colors?: string[]
  data: Highcharts.SeriesLineOptions[]
  legend?: unknown
  plotBackgroundColor?: string
  plotBands?: unknown
  plotLines?: unknown
  stackingStyle?: Highcharts.OptionsStackingValue
  title?: string
  toolTipFormatter?: Highcharts.TooltipFormatterCallbackFunction
  toolTipUnit?: string
  xAxis?: unknown
  xAxisLabel?: string
  xAxisLabelFormatter?: Highcharts.AxisLabelsFormatterCallbackFunction
  xAxisTickWidth?: number
  xAxisVisible?: boolean
  yAxis?: unknown
  yAxisGridLineColor?: string
  yAxisLabel?: string
  yAxisLabelFormatter?: Highcharts.AxisLabelsFormatterCallbackFunction
  yAxisVisible?: boolean
  yRange?: number[]
}

interface Signature {
  Element: HTMLDivElement
  Args: Args
}

const disabled = { enabled: false }
export class SeriesLine extends Component<Signature> {
  get chartMargins() {
    if (!this.args.chartMargin) return { margin: undefined }

    const [marginTop, marginRight, marginBottom, marginLeft] = this.args.chartMargin

    return {
      marginTop: marginTop ?? undefined,
      marginRight: marginRight ?? undefined,
      marginBottom: marginBottom ?? undefined,
      marginLeft: marginLeft ?? undefined,
    }
  }

  get chartOptions(): Highcharts.Options {
    return {
      colors: this.colors,
      chart: {
        type: 'line',
        plotBackgroundColor: this.plotBackgroundColor,
        backgroundColor: 'transparent',
        spacing: this.chartSpacing,
        ...this.chartMargins,
      },
      credits: disabled,
      title: this.title,
      yAxis: this.yAxis,
      xAxis: this.xAxis,
      plotOptions: this.plotOptions,
      tooltip: this.toolTip,
      legend: this.legend,
    }
  }

  get chartMargin() {
    return this.args.chartMargin
  }

  get chartSpacing() {
    return this.args.chartSpacing ?? [10, 10, 10, 10]
  }

  get colors() {
    return this.args.colors
  }

  get highchartsOptions(): Highcharts.Options {
    return { ...this.chartOptions, series: this.args.data }
  }

  get legend() {
    return this.args.legend ?? disabled
  }

  get plotBackgroundColor() {
    return this.args.plotBackgroundColor ?? colors.dustyBlack50
  }

  get plotOptions(): Highcharts.PlotOptions {
    return {
      line: {
        stacking: this.args.stackingStyle,
        animation: this.args.animation,
        marker: {
          radius: 8,
        },
      },
    }
  }

  get plotBands() {
    return this.args.plotBands ?? []
  }

  get plotLines() {
    return this.args.plotLines ?? []
  }

  get title() {
    if (typeof this.args.title === 'string') {
      return reportingTitle(this.args.title)
    }
    return this.args.title
  }

  get toolTip() {
    const formatter = this.args.toolTipFormatter

    return toolTipConfig('y', this.args.toolTipUnit, { formatter, backgroundColor: colors.neutral400 }) ?? disabled
  }

  get toolTipUnit() {
    return this.args.toolTipUnit ?? ''
  }

  get xAxis() {
    const allowDecimals = false

    const xAxis: Record<string, unknown> = {
      title: reportingTitle(this.xAxisLabel),
      visible: this.xAxisVisible,
      categories: this.args.categories,
      allowDecimals,
      tickWidth: this.xAxisTickWidth,
      lineColor: colors.dustyBlack200,
      tickColor: colors.dustyBlack200,
    }

    if (this.args.xAxisLabelFormatter) {
      xAxis.labels = {
        formatter: this.args.xAxisLabelFormatter,
      }
    }

    return xAxis
  }

  get xAxisLabel() {
    return this.args.xAxisLabel ?? ''
  }

  get xAxisTickWidth() {
    return this.args.xAxisTickWidth ?? 1
  }

  get xAxisVisible() {
    return this.args.xAxisVisible ?? true
  }

  get yAxis() {
    if (this.args.yAxis) return this.args.yAxis

    const [min, max] = this.yRange

    const allowDecimals = false

    const yAxis: Record<string, unknown> = {
      title: reportingTitle(this.yAxisLabel),
      visible: this.yAxisVisible,
      min,
      max,
      plotLines: this.plotLines,
      plotBands: this.plotBands,
      allowDecimals,
      gridLineColor: this.yAxisGridLineColor,
    }

    if (min) {
      yAxis.startOnTick = false
    }

    if (max) {
      yAxis.endOnTick = false
    }

    if (this.args.yAxisLabelFormatter) {
      yAxis.labels = {
        formatter: this.args.yAxisLabelFormatter,
      }
    }

    return yAxis
  }

  get yAxisLabel() {
    return this.args.yAxisLabel ?? ''
  }

  get yAxisVisible() {
    return this.args.yAxisVisible ?? true
  }

  get yRange() {
    return this.args.yRange ?? []
  }

  get yAxisGridLineColor() {
    return this.args.yAxisGridLineColor ?? colors.dustyBlack100
  }

  <template><BaseHighcharterComponent class="h-full" @options={{this.highchartsOptions}} ...attributes /></template>
}

export default SeriesLine
